/**
 * Valid statuses:
 *
 * `cancelled`: This status is self-explanatory, indicating that the loop has been canceled.
 * `completed-with-errors`: If a fatal error occurs during the loop execution.
 * `completed`: Indicates that the loop has finished its execution successfully.
 * `in-progress`: Indicates that the loop is currently being executed.
 * `not-completed`: If the recipe hasn't been fully triggered yet. E.g. two triggers but only one has been fired.
 * `paused`: Indicates that the loop has been temporarily paused.
 * `queued`: Indicates that the current loop is scheduled to start at a later time.
 * `skipped`: This status is assigned when the loop has no users/posts to iterate.
 */

import './in-progress';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-loop-status' )
export class AutomatorLogItemLoopStatus extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	// The post ID of the loop
	@property( { type: Number, attribute: 'loop-id' } ) accessor loopID;

	// A unique code created to detect this specific loop run in this specific recipe run
	// The backend will use this to cancel/pause/resume the loop
	@property( { type: String, attribute: 'loop-process-code' } ) accessor processCode;

	@property( { type: String, attribute: 'status-id' } ) accessor statusID;

	@property( { type: Number, attribute: 'elements-completed' } ) accessor elementsCompleted = 0;

	@property( { type: Number, attribute: 'elements-total' } ) accessor elementsTotal;

	// Any message that the backend could have returned about this loop
	// This will be used to show errors too
	@property( { type: String, attribute: false } ) accessor message = '';

	// The date the loop started running
	@property( { type: String, attribute: 'start-date' } ) accessor startDate;

	// The date the loop finished running
	@property( { type: String, attribute: 'end-date' } ) accessor endDate;

	// The difference between both start and end dates
	@property( { type: Object, attribute: false } ) accessor dateElapsed;

	// Information about when the next batch of this loop will run
	@property( { type: Object, attribute: false } ) accessor dateNextProcess;

	// This property is used to indicate to the web component that it shouldn't try to retrieve data from the server
	// and that it should use the data provided when it was created
	@property( { type: Boolean, attribute: 'import-mode' } ) accessor isImportMode = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		if ( this.statusID === 'in-progress' ) {
			return html`
				<uap-log-loop-status-in-progress
					loop-id=${ this.loopID }
					loop-process-code=${ this.processCode }

					elements-completed=${ this.elementsCompleted }
					elements-total=${ this.elementsTotal }
					start-date=${ this.startDate }

					.dateElapsed=${ this.dateElapsed }
					.dateNextProcess=${ this.dateNextProcess }

					?import-mode=${ this.isImportMode }
				></uap-log-loop-status-in-progress>
			`;
		}

		return html`
			<div class="progress progress--${ this.statusID }">
				<div class="progress-info">
					${ this._templateStatus }
					${ this._templateNumberProcessed }
				</div>

				<div class="progress-details">
					${ this._templateDate }
					${ this._templateMessage }
				</div>
			</div>
		`;
	}

	/**
	 * Creates the status icon and text
	 *
	 * @return {TemplateResult} The status
	 */
	get _templateStatus() {
		if ( this.statusID === 'completed' ) {
			return html`
				<span class="progress-info-status">
					<uap-log-status status-id=${ this.statusID } hide-label></uap-log-status>
				</span>
			`;
		}

		return html`
			<span class="progress-info-status">
				<uap-log-status status-id=${ this.statusID }></uap-log-status>
			</span>
		`;
	}

	/**
	 * Render the number of elements processed
	 *
	 * @return {TemplateResult|undefined} The number of elements processed
	 */
	get _templateNumberProcessed() {
		// Check if there is data available
		if ( isEmpty( this.elementsTotal ) ) {
			return;
		}

		// Check if this was partially completed
		if ( [ 'cancelled', 'paused', 'stopped' ].includes( this.statusID ) ) {
			return html`
				<div class="progress-info-text">
					<span class="progress-info-completed">
						${ sprintf(
							/* translators: 1. Number of items processed. 2. Total number of items */
							escapeHTML( __( '%1$s/%2$s processed', 'uncanny-automator' ) ),
							this.elementsCompleted.toLocaleString(),
							this.elementsTotal.toLocaleString()
						) }
					</span>
					<span class="progress-info-percentage">
						(${ this.percentage }%)
					</span>
				</div>
			`;
		}

		// Queued loops haven't started yet — show item count without "processed"
		if ( 'queued' === this.statusID ) {
			return html`
				<div class="progress-info-text">
					<span class="progress-info-completed">
						${ sprintf(
							/* translators: 1. Number of items */
							escapeHTML( __( '%1$s items', 'uncanny-automator' ) ),
							this.elementsTotal.toLocaleString()
						) }
					</span>
				</div>
			`;
		}

		// Completed and other statuses — show total processed
		return html`
			<div class="progress-info-text">
				<span class="progress-info-completed">
					${ sprintf(
						/* translators: 1. Number of items processed */
						escapeHTML( __( '%1$s processed', 'uncanny-automator' ) ),
						this.elementsTotal.toLocaleString()
					) }
				</span>
			</div>
		`;
	}

	/**
	 * Returns the HTML of the section that shows the start and end date
	 *
	 * @return {TemplateResult|undefined} The date
	 */
	get _templateDate() {
		// Check if the item hasn't run yet
		if ( isEmpty( this.startDate ) ) {
			return;
		}

		// If completed, show the relative date and hide the start and end date in a tooltip
		if (
			this.statusID === 'completed' &&
			! isEmpty( this.dateElapsed ) &&
			! isEmpty( this.dateElapsed.human_time_diff )
		) {
			return html`
				<uo-tooltip>
					${ sprintf(
						/* translators: 1 and 2 are dates */
						escapeHTML( __( '%1$s to %2$s', 'uncanny-automator' ) ),
						this.startDate,
						this.endDate
					) }

					<div slot="target">
						<uo-icon id="clock"></uo-icon>
						${ escapeHTML( __( 'Elapsed:', 'uncanny-automator' ) ) } ${ this.dateElapsed.human_time_diff }
					</div>
				</uo-tooltip>
			`;
		}

		// Otherwise, just show the start date
		return html`
			<div>
				<uo-icon id="clock"></uo-icon>
				${ this.startDate }
			</div>
		`;
	}

	/**
	 * Returns the template that shows the status message, if available.
	 *
	 * @return {TemplateResult|undefined} The date or undefined if the date shouldn't be shown
	 */
	get _templateMessage() {
		// Check if the item hasn't run yet
		if ( isEmpty( this.message ) ) {
			return;
		}

		return html`
			<div>
				<uo-icon id="note"></uo-icon>
				${ this.message }
			</div>
		`;
	}

	/**
	 * Calculates the percentage using the completed and total elements
	 *
	 * @return {Number} The percentage
	 */
	get percentage() {
		return Math.round( 100 * this.elementsCompleted / this.elementsTotal );
	}
}